import React, { useState } from "react";
import DOMPurify from "dompurify";
import VideoEmbed from "./VideoEmbed";
import Lightbox from "./Lightbox";
import PdfDownloadGate from "./PdfDownloadGate";
import { Quote as QuoteIcon, FileText, Download } from "lucide-react";
import { INSIGHT_PALETTE } from "@/admin/components/InsightBlocksEditor";

/**
 * Public renderer for Insight article body blocks.
 * See /app/frontend/src/admin/components/InsightBlocksEditor.jsx for the
 * matching editor and block schemas.
 */

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  return u;
}

const RICHTEXT_OPTS = {
  ALLOWED_TAGS: ["p","h2","h3","h4","strong","em","u","a","ul","ol","li","br","code","blockquote","span"],
  ALLOWED_ATTR: ["href","target","rel","class","style"],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|\/)/i,
};

const paletteFor = (key) => INSIGHT_PALETTE.find((c) => c.key === key) || INSIGHT_PALETTE[0];

export default function InsightBlocks({ blocks, insightSlug }) {
  const [lightbox, setLightbox] = useState(null);
  // PDF gate state: which PDF block is currently prompting the lead form.
  const [pdfGate, setPdfGate] = useState(null); // {block_id, url, title}
  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  return (
    <>
      <div data-testid="insight-blocks-render">
        {blocks.map((b, i) => {
          const gap = { marginBottom: `${Number.isFinite(Number(b.spacing_px)) ? Number(b.spacing_px) : 24}px` };

          if (b.type === "richtext") {
            const safe = DOMPurify.sanitize(b.html || "", RICHTEXT_OPTS);
            return (
              <div key={b.id || i} style={gap} data-testid={`ib-richtext-${i}`}>
                <div className="article-content" dangerouslySetInnerHTML={{ __html: safe }} />
              </div>
            );
          }

          if (b.type === "youtube") {
            if (!b.url) return null;
            return (
              <figure key={b.id || i} style={gap} data-testid={`ib-youtube-${i}`}>
                <VideoEmbed src={b.url} title={b.caption || ""} aspect="aspect-video" />
                {b.caption && <figcaption className="text-xs text-neutral-500 mt-2 italic">{b.caption}</figcaption>}
              </figure>
            );
          }

          if (b.type === "image") {
            if (!b.src) return null;
            return (
              <figure key={b.id || i} style={gap} data-testid={`ib-image-${i}`}>
                <button type="button"
                  onClick={() => setLightbox({ src: resolveSrc(b.src), alt: b.alt || "" })}
                  className="group block w-full text-left"
                  title={b.alt || ""}>
                  <img src={resolveSrc(b.src)} alt={b.alt || ""} loading="lazy"
                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.01]" />
                </button>
                {b.caption && <figcaption className="text-xs text-neutral-500 mt-2 italic">{b.caption}</figcaption>}
              </figure>
            );
          }

          if (b.type === "colored_list") {
            const c = paletteFor(b.color_key);
            const items = Array.isArray(b.items) ? b.items.filter(Boolean) : [];
            if (items.length === 0 && !b.title) return null;
            return (
              <div key={b.id || i} style={{ ...gap, background: c.bg, color: c.text }}
                className="p-6 md:p-8 rounded-md" data-testid={`ib-colored-list-${i}`}>
                {b.title && <div className="font-display text-2xl md:text-3xl mb-4">{b.title}</div>}
                <ul className="space-y-2.5">
                  {items.map((it, k) => (
                    <li key={k} className="flex gap-3 text-base md:text-lg leading-snug">
                      <span className="font-display shrink-0 opacity-70">{String(k + 1).padStart(2, "0")}</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          if (b.type === "quote") {
            if (!b.text) return null;
            return (
              <blockquote key={b.id || i} style={gap} data-testid={`ib-quote-${i}`}
                className="my-6 border-l-4 border-accent-yellow pl-5 md:pl-8">
                <QuoteIcon className="w-6 h-6 md:w-8 md:h-8 text-accent-yellow mb-3" />
                <p className="font-display text-2xl md:text-4xl leading-[1.15] text-black">{b.text}</p>
                {b.author && <footer className="mt-4 text-sm text-neutral-500">— {b.author}</footer>}
              </blockquote>
            );
          }

          if (b.type === "audio") {
            if (!b.src) return null;
            return (
              <div key={b.id || i} style={gap} data-testid={`ib-audio-${i}`}
                className="bg-neutral-50 border border-black/10 rounded-md p-4 md:p-5">
                {b.title && <div className="text-sm font-semibold mb-2 text-black">{b.title}</div>}
                <audio controls src={resolveSrc(b.src)} className="w-full" preload="none" />
              </div>
            );
          }

          if (b.type === "pdf") {
            if (!b.src && !b.title) return null;
            const href = resolveSrc(b.src);
            const thumb = resolveSrc(b.thumb);
            // Clicking triggers the lead-gate modal instead of a direct download.
            // The modal captures Name / Email / WhatsApp, persists a Lead, and
            // (optionally) emails a branded copy with the download link.
            return (
              <button
                type="button"
                key={b.id || i}
                onClick={() => setPdfGate({ block_id: b.id, url: href, title: b.title || "Material Places" })}
                style={gap}
                data-testid={`ib-pdf-${i}`}
                className="group w-full text-left flex items-center gap-5 md:gap-6 border border-black/10 rounded-md p-4 md:p-5 bg-neutral-50 hover:bg-white hover:border-black/30 hover:shadow-md transition-all"
              >
                <div className="shrink-0 relative w-20 md:w-24 aspect-[3/4] overflow-hidden rounded bg-black/5 border border-black/10">
                  {thumb ? (
                    <img src={thumb} alt={b.title || "PDF"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent-orange/10 to-accent-yellow/20">
                      <FileText className="w-8 h-8 md:w-10 md:h-10 text-accent-orange" />
                    </div>
                  )}
                  <span className="absolute bottom-1 left-1 right-1 text-center text-[9px] uppercase tracking-widest font-bold bg-black text-white rounded-sm py-0.5">PDF</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-accent-orange font-semibold mb-1">Material em PDF</div>
                  <div className="font-display text-lg md:text-2xl leading-snug text-black group-hover:text-accent-orange transition-colors">{b.title || "Baixar PDF"}</div>
                  {b.caption && <div className="text-xs md:text-sm text-neutral-500 mt-1.5">{b.caption}</div>}
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-black underline underline-offset-4 decoration-accent-yellow decoration-2 group-hover:decoration-accent-orange">
                    <Download className="w-3.5 h-3.5" /> Baixar
                  </div>
                </div>
              </button>
            );
          }

          return null;
        })}
      </div>
      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
      <PdfDownloadGate
        open={!!pdfGate}
        onClose={() => setPdfGate(null)}
        insightSlug={insightSlug}
        blockId={pdfGate?.block_id}
        fileUrl={pdfGate?.url}
        fileTitle={pdfGate?.title}
      />
    </>
  );
}
